import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import CustomConfirm from "ra-custom-confirm";
import Check from "@material-ui/icons/Check";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import RefreshIcon from "@material-ui/icons/Refresh";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";

const CARDHOLDER_REF_PREFIX = "SPRIVEMaster";
const ACTIVE = "active";
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

const STATUS_LABELS: Record<string, string> = {
  pending: "Reserved",
  cardholder_registered: "Cardholder registered",
  account_created: "Account created",
  active: "Active",
};

interface MasterAccount {
  id: number;
  sequence_no: number;
  cardholder_ref: string;
  ext_account_no: string;
  account_no?: string | null;
  bank_account_no?: string | null;
  sort_code?: string | null;
  product_class: string;
  status: string;
  last_error_code?: string | null;
  last_error?: string | null;
  updatedBy?: string | null;
  updatedAt?: string;
}

const useStyles = makeStyles((theme) => ({
  infoBox: {
    backgroundColor: "#fff3e0",
    border: "1px solid #ffb74d",
    borderRadius: "8px",
    padding: "1em",
    marginBottom: "1.5em",
  },
  infoText: {
    color: "#e65100",
    fontSize: "0.9rem",
  },
  actions: {
    display: "flex",
    gap: "1em",
    marginBottom: "1.5em",
  },
  button: {
    padding: "12px 24px",
    fontSize: "0.95rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
  },
  tableWrapper: {
    overflowX: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
  },
  headCell: {
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  mono: {
    fontFamily: "monospace",
    whiteSpace: "nowrap",
  },
  activeChip: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
  },
  pendingChip: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.8rem",
    maxWidth: "260px",
  },
  empty: {
    padding: "2em",
    textAlign: "center",
  },
}));

const formatSortCode = (sortCode?: string | null) =>
  sortCode && sortCode.length === 6 ? sortCode.replace(/(\d{2})(\d{2})(\d{2})/, "$1-$2-$3") : sortCode || "—";

const isValidAmount = (value: string) => AMOUNT_PATTERN.test(value.trim()) && Number(value) > 0;

const formatDate = (value?: string) => (value ? new Date(value).toLocaleString("en-GB") : "—");

const PpsMasterAccounts = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [rows, setRows] = useState<MasterAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [returnTarget, setReturnTarget] = useState<MasterAccount | null>(null);
  const [returnAmount, setReturnAmount] = useState("");
  const [returning, setReturning] = useState(false);

  const unfinished = rows.find((row) => row.status !== ACTIVE);
  const nextSequence = rows.reduce((max, row) => Math.max(max, row.sequence_no), 0) + 1;
  const target = unfinished ? unfinished.cardholder_ref : `${CARDHOLDER_REF_PREFIX}${nextSequence}`;

  const fetchRows = () => {
    setLoading(true);
    axios
      .get(ENDPOINTS.PPS_MASTER_ACCOUNTS, getConfigGet({ $limit: 50 }))
      .then((res: any) => {
        setRows(res?.data?.data || []);
      })
      .catch((error) => {
        notify(error?.response?.data?.message || "Failed to fetch master accounts", "error", "", undefined, 5000);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = () => {
    setConfirmOpen(false);
    setCreating(true);
    axios
      .post(ENDPOINTS.PPS_MASTER_ACCOUNTS, {}, { headers: getConfigPost() })
      .then((res: any) => {
        notify(`${res?.data?.cardholder_ref || target} is active`, "info", "", undefined, 10000);
      })
      .catch((error) => {
        notify(error?.response?.data?.message || `Failed to create ${target}`, "error", "", undefined, 10000);
      })
      .finally(() => {
        setCreating(false);
        fetchRows();
      });
  };

  const openReturn = (row: MasterAccount) => {
    setReturnAmount("");
    setReturnTarget(row);
  };

  const closeReturn = () => {
    if (!returning) setReturnTarget(null);
  };

  const handleReturn = () => {
    if (!returnTarget || !isValidAmount(returnAmount)) return;
    const ref = returnTarget.cardholder_ref;
    setReturning(true);
    axios
      .post(
        ENDPOINTS.PPS_MASTER_ACCOUNT_RETURNS,
        { pps_master_account_id: returnTarget.id, amount: Number(returnAmount) },
        { headers: getConfigPost() }
      )
      .then((res: any) => {
        notify(`Returned £${Number(returnAmount).toFixed(2)} from ${ref} to master 0 (PPS ref ${res?.data?.txn_ref})`, "info", "", undefined, 15000);
        setReturnTarget(null);
      })
      .catch((error) => {
        notify(error?.response?.data?.message || `Failed to return funds from ${ref}`, "error", "", undefined, 15000);
      })
      .finally(() => setReturning(false));
  };

  return (
    <>
      <Box className={classes.infoBox}>
        <Typography className={classes.infoText}>
          Creates the next PPS master account under the master product class: registers the cardholder, creates the
          account and allocates its sort code and account number. If a step fails, the error is shown below and the next
          click resumes the same account from where it stopped.
        </Typography>
      </Box>

      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          disabled={creating || loading}
          onClick={() => setConfirmOpen(true)}
        >
          {creating && <CircularProgress size={18} thickness={2} style={{ marginRight: "10px", color: "white" }} />}
          {unfinished ? `Resume ${target}` : `Create ${target}`}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          className={classes.button}
          disabled={creating || loading}
          onClick={fetchRows}
          startIcon={<RefreshIcon />}
        >
          Refresh
        </Button>
      </div>

      <div className={classes.tableWrapper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {["#", "Cardholder ref", "Account ref", "PPS account no", "Sort code", "Bank account no", "Status", "Last error", "Updated", "Actions"].map(
                (label) => (
                  <TableCell key={label} className={classes.headCell}>
                    {label}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.sequence_no}</TableCell>
                <TableCell className={classes.mono}>{row.cardholder_ref}</TableCell>
                <TableCell className={classes.mono}>{row.ext_account_no}</TableCell>
                <TableCell className={classes.mono}>{row.account_no || "—"}</TableCell>
                <TableCell className={classes.mono}>{formatSortCode(row.sort_code)}</TableCell>
                <TableCell className={classes.mono}>{row.bank_account_no || "—"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={STATUS_LABELS[row.status] || row.status}
                    className={row.status === ACTIVE ? classes.activeChip : classes.pendingChip}
                  />
                </TableCell>
                <TableCell>
                  {row.last_error ? (
                    <Typography className={classes.errorText}>
                      {row.last_error_code ? `${row.last_error_code}: ` : ""}
                      {row.last_error}
                    </Typography>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell style={{ whiteSpace: "nowrap" }}>
                  {formatDate(row.updatedAt)}
                  {row.updatedBy ? ` · ${row.updatedBy}` : ""}
                </TableCell>
                <TableCell>
                  {row.status === ACTIVE ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      style={{ whiteSpace: "nowrap", textTransform: "none" }}
                      disabled={creating || loading || returning}
                      onClick={() => openReturn(row)}
                    >
                      Return to master 0
                    </Button>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className={classes.empty}>
                  No additional master accounts yet.
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={10} className={classes.empty}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!returnTarget} onClose={closeReturn} aria-labelledby="return-dialog-title" fullWidth maxWidth="sm">
        <DialogTitle id="return-dialog-title">Return funds from {returnTarget?.cardholder_ref} to master 0</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Moves money from <strong>{returnTarget?.cardholder_ref}</strong> back to master 0 with an internal PPS
            transfer. It cannot be undone from here, and it cannot exceed the master's available balance.
          </DialogContentText>
          <DialogContentText>
            This only moves the money. Transfers still assigned to this master must be released to master 0 separately.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            label="Amount (£)"
            value={returnAmount}
            onChange={(event) => setReturnAmount(event.target.value)}
            error={returnAmount !== "" && !isValidAmount(returnAmount)}
            helperText={returnAmount !== "" && !isValidAmount(returnAmount) ? "Enter a positive amount with at most 2 decimals" : " "}
            disabled={returning}
            inputProps={{ inputMode: "decimal" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReturn} color="primary" disabled={returning}>
            Cancel
          </Button>
          <Button
            onClick={handleReturn}
            color="primary"
            variant="contained"
            disabled={returning || !isValidAmount(returnAmount)}
          >
            {returning ? (
              <CircularProgress size={20} />
            ) : isValidAmount(returnAmount) ? (
              `Return £${Number(returnAmount).toFixed(2)}`
            ) : (
              "Return"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomConfirm
        isOpen={confirmOpen}
        title={unfinished ? `Resume ${target}` : `Create ${target}`}
        user={{ target, resuming: !!unfinished }}
        content={ConfirmContent}
        confirm={unfinished ? "Resume" : "Create"}
        confirmColor="primary"
        ConfirmIcon={Check}
        cancel="Cancel"
        CancelIcon={ErrorOutline}
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};

const ConfirmContent = (props: any) => {
  const data = props.user;
  if (!data) return null;

  return (
    <div style={{ padding: "1.5em" }}>
      <Typography variant="body1" gutterBottom>
        {data.resuming ? (
          <>
            This continues creating <strong>{data.target}</strong> from the step it stopped at.
          </>
        ) : (
          <>
            This creates a new PPS master account <strong>{data.target}</strong>.
          </>
        )}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        A real account is opened at PPS and cannot be undone from here.
      </Typography>
    </div>
  );
};

export default PpsMasterAccounts;
